import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

const DemoModal = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const demoSteps = [
    {
      title: "Simple Invoice Creation",
      description: "Create FBR-compliant invoices with our intuitive interface",
      content: (
        <div className="bg-gray-100 rounded-lg p-6 h-64 flex items-center justify-center">
          <div className="text-center">
            <div className="bg-white rounded-lg shadow-md p-4 max-w-sm mx-auto">
              <h3 className="font-semibold text-gray-900 mb-2">New Invoice</h3>
              <div className="space-y-2 text-left">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Customer:</span>
                  <span className="text-gray-900">ABC Company</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Amount:</span>
                  <span className="text-gray-900">Rs. 50,000</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Tax:</span>
                  <span className="text-gray-900">Rs. 9,000</span>
                </div>
                <hr className="my-2" />
                <div className="flex justify-between font-semibold">
                  <span>Total:</span>
                  <span>Rs. 59,000</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Real-time FBR Submission",
      description: "Submit directly to FBR with instant confirmation",
      content: (
        <div className="bg-gray-100 rounded-lg p-6 h-64 flex items-center justify-center">
          <div className="text-center">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 max-w-sm mx-auto">
              <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mx-auto mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <h3 className="font-semibold text-green-900 mb-2">Successfully Submitted</h3>
              <p className="text-sm text-green-700 mb-2">Invoice #INV-2024-001</p>
              <p className="text-xs text-green-600">FBR Response: ACCEPTED</p>
              <p className="text-xs text-green-600">UUID: 12345-67890-ABCDE</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Auto QR Code Generation",
      description: "Automatic QR codes and professional invoice printing",
      content: (
        <div className="bg-gray-100 rounded-lg p-6 h-64 flex items-center justify-center">
          <div className="text-center">
            <div className="bg-white rounded-lg shadow-md p-4 max-w-sm mx-auto">
              <h3 className="font-semibold text-gray-900 mb-4">Invoice with QR Code</h3>
              <div className="flex items-center justify-between">
                <div className="text-left text-sm">
                  <p className="font-medium">ABC Company</p>
                  <p className="text-gray-600">Invoice #INV-001</p>
                  <p className="text-gray-600">Amount: Rs. 59,000</p>
                </div>
                <div className="w-16 h-16 bg-gray-800 rounded flex items-center justify-center">
                  <div className="w-12 h-12 bg-white rounded grid grid-cols-3 gap-px">
                    {[...Array(9)].map((_, i) => (
                      <div key={i} className={`${Math.random() > 0.5 ? 'bg-black' : 'bg-white'}`}></div>
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">Scan QR for verification</p>
            </div>
          </div>
        </div>
      )
    }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-900">FBRSTAPP Demo</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Step Navigation */}
          <div className="flex justify-center mb-6">
            <div className="flex space-x-2">
              {demoSteps.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentStep(index)}
                  className={`w-3 h-3 rounded-full ${
                    index === currentStep ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Current Step Content */}
          <div className="text-center mb-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {demoSteps[currentStep].title}
            </h3>
            <p className="text-gray-600 mb-6">
              {demoSteps[currentStep].description}
            </p>
            
            {demoSteps[currentStep].content}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center">
            <button
              onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
              disabled={currentStep === 0}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            
            <span className="text-sm text-gray-500">
              {currentStep + 1} of {demoSteps.length}
            </span>
            
            {currentStep < demoSteps.length - 1 ? (
              <button
                onClick={() => setCurrentStep(Math.min(demoSteps.length - 1, currentStep + 1))}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Next
              </button>
            ) : (
              <button
                onClick={onClose}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Get Started
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoModal;